<template>
  <v-container class="mis-pedidos-page">
    <div class="mb-2">
      <v-btn text color="primary" class="px-2 back-btn" :ripple="false" @click="goBack">
        <v-icon left>mdi-arrow-left</v-icon>
        Volver
      </v-btn>
    </div>

    <h1 class="page-title">Mis pedidos</h1>
    <p class="page-subtitle">{{ pageSubtitle }}</p>

    <v-alert v-if="!isLoggedIn" type="info" outlined dense class="mt-4">
      Iniciá sesión para ver tu historial de pedidos.
      <div class="mt-3">
        <v-btn color="primary" small rounded @click="goCuenta">Ir a mi cuenta</v-btn>
      </div>
    </v-alert>

    <div v-else class="mt-4">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" rounded></v-progress-linear>

      <v-alert v-if="!loading && error" type="error" dense outlined>
        {{ error }}
      </v-alert>

      <v-alert v-else-if="!loading && pedidos.length === 0" type="info" dense outlined>
        {{ emptyMessage }}
      </v-alert>

      <div
        v-for="p in pedidos"
        :key="p.Id"
        class="pedido-card"
      >
        <div class="pedido-top">
          <div class="pedido-meta">
            <div class="pedido-tienda" v-if="p.TiendaNombre">
              <v-icon x-small class="mr-1">mdi-storefront-outline</v-icon>
              {{ p.TiendaNombre }}
            </div>
            <div class="pedido-id">Pedido #{{ p.Id }}</div>
            <div class="pedido-fecha">{{ formatFecha(p.Fecha) }}</div>
          </div>
          <v-chip
            x-small
            class="estado-chip"
            :color="estadoColor(p)"
            :text-color="estadoTextColor(p)"
            label
          >
            {{ p.Estado || 'Sin estado' }}
          </v-chip>
        </div>

        <div class="pedido-total">$ {{ formatMoney(p.Total) }}</div>

        <div class="pedido-lineas">
          <div
            v-for="(l, idx) in (p.Lineas || []).slice(0, 3)"
            :key="idx"
            class="linea-resumen"
          >
            <span class="linea-cant">{{ formatCant(l.Cantidad) }}×</span>
            {{ l.Nombre }}
          </div>
          <div v-if="(p.Lineas || []).length > 3" class="linea-mas">
            +{{ p.Lineas.length - 3 }} más
          </div>
        </div>

        <div class="pedido-actions">
          <v-btn text color="primary" class="action-text" @click="verPedido(p)">
            Ver estado
          </v-btn>
          <v-btn
            color="primary"
            rounded
            class="action-primary"
            :loading="repeatingId === p.Id"
            @click="repetirPedido(p)"
          >
            Repetir pedido
          </v-btn>
        </div>
      </div>

      <v-snackbar v-model="snack.show" :color="snack.color" timeout="3500">
        {{ snack.text }}
      </v-snackbar>
    </div>
  </v-container>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { getGlobalAccountClientCode } from '@/helpers/globalAccountClientCode'

export default {
  name: 'MisPedidos',
  data () {
    return {
      loading: false,
      pedidos: [],
      error: null,
      repeatingId: null,
      snack: { show: false, text: '', color: 'success' }
    }
  },
  computed: {
    ...mapGetters('Cuenta', ['isLoggedIn']),
    isGlobal () {
      return this.$route.name === 'MisPedidosGlobal' || !this.$route.params.client
    },
    pageSubtitle () {
      return this.isGlobal
        ? 'Historial de todas las tiendas de tu cuenta'
        : 'Historial y repetición de pedidos de esta tienda'
    },
    emptyMessage () {
      return this.isGlobal
        ? 'Todavía no tenés pedidos con tu cuenta.'
        : 'Todavía no tenés pedidos en esta tienda con tu cuenta.'
    }
  },
  async mounted () {
    if (this.isLoggedIn) await this.load()
  },
  watch: {
    async isLoggedIn (v) {
      if (v) await this.load()
      else this.pedidos = []
    }
  },
  methods: {
    ...mapActions('Cuenta', ['fetchMisPedidos']),
    ...mapActions('Carrito', ['addItem', 'resetCarrito']),
    goBack () {
      if (this.isGlobal) {
        this.$router.push({ name: 'Tiendas' }).catch(() => {})
        return
      }
      const client = this.$route.params.client
      if (client) this.$router.push({ name: 'Home', params: { client } })
      else this.$router.push({ name: 'CuentaGlobal' })
    },
    goCuenta () {
      if (this.isGlobal) {
        this.$router.push({ name: 'CuentaGlobal', query: { from: 'tiendas' } }).catch(() => {})
        return
      }
      const client = this.$route.params.client
      this.$router.push({
        name: 'CuentaGlobal',
        query: { from: 'store', client, home: 'Home' }
      }).catch(() => {})
    },
    verPedido (p) {
      const client = (p && p.TiendaCodigo) || this.$route.params.client
      if (!client) {
        this.snack = { show: true, color: 'warning', text: 'No se pudo determinar la tienda del pedido.' }
        return
      }
      this.$router.push({ name: 'VerPedido', params: { client, id: p.Id } })
    },
    estadoColor (p) {
      if (p && p.EstadoColor) return p.EstadoColor
      const e = (p && p.Estado ? String(p.Estado) : '').toLowerCase()
      if (e.includes('rechaz') || e.includes('cancel')) return 'error'
      if (e.includes('confirm') || e.includes('entreg') || e.includes('listo')) return 'success'
      if (e.includes('pend')) return 'warning'
      return 'grey lighten-3'
    },
    estadoTextColor (p) {
      if (p && p.EstadoColor) return 'white'
      const e = (p && p.Estado ? String(p.Estado) : '').toLowerCase()
      if (e.includes('rechaz') || e.includes('cancel') || e.includes('confirm') || e.includes('entreg') || e.includes('listo') || e.includes('pend')) {
        return 'white'
      }
      return undefined
    },
    formatFecha (f) {
      if (!f) return ''
      const d = new Date(f)
      if (Number.isNaN(d.getTime())) return String(f)
      return d.toLocaleString('es-AR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatMoney (n) {
      return Number(n || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      })
    },
    formatCant (n) {
      const v = Number(n) || 0
      return Number.isInteger(v) ? String(v) : v.toFixed(1)
    },
    async load () {
      this.loading = true
      this.error = null
      try {
        const clientCode = this.isGlobal
          ? getGlobalAccountClientCode()
          : (this.$route.params.client || this.$clientCode())
        const data = await this.fetchMisPedidos({ clientCode })
        this.pedidos = Array.isArray(data) ? data : []
      } catch (e) {
        this.error = 'No se pudo cargar el historial.'
        this.pedidos = []
      } finally {
        this.loading = false
      }
    },
    async repetirPedido (pedido) {
      this.repeatingId = pedido.Id
      try {
        const lineas = pedido.Lineas || []
        const simples = lineas.filter(l => l.Tipo === 1 && l.Disponible)
        const omitidos = lineas.filter(l => l.Tipo !== 1 || !l.Disponible)

        if (simples.length === 0) {
          this.snack = {
            show: true,
            color: 'warning',
            text: omitidos.length
              ? 'Los ítems de este pedido requieren configuración o no están disponibles. Entrá al detalle de cada producto.'
              : 'No hay productos disponibles para repetir.'
          }
          return
        }

        const client = pedido.TiendaCodigo || this.$route.params.client
        if (!client) {
          this.snack = {
            show: true,
            color: 'warning',
            text: 'No se pudo determinar la tienda para repetir el pedido.'
          }
          return
        }

        this.resetCarrito()
        simples.forEach(l => {
          this.addItem({
            id: l.ProductoId,
            nombre: l.Nombre,
            tipo: l.Tipo,
            cant: Number(l.Cantidad) || 1,
            totalPrice: Number(l.PrecioUnitario) || 0,
            PrecioUnitario: Number(l.PrecioUnitario) || 0,
            items: [],
            esCombinadoCombo: false,
            esSeleccion: false,
            detalle: { srcImg: l.SrcImg },
            obs: l.Observacion || null,
            Descuentos: [],
            GrupoDescuento: null
          })
        })

        let msg = `Se agregaron ${simples.length} producto(s) al carrito.`
        if (omitidos.length) {
          msg += ` ${omitidos.length} ítem(s) se omitieron (combo o no disponible).`
        }
        this.snack = { show: true, color: 'success', text: msg }

        this.$router.push({ name: 'Home', params: { client } })
      } finally {
        this.repeatingId = null
      }
    }
  }
}
</script>
<style scoped>
.mis-pedidos-page {
  max-width: 640px;
}
.back-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}
.page-subtitle {
  margin: 4px 0 0;
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.9rem;
}
.pedido-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 14px 16px 12px;
  margin-bottom: 12px;
}
.pedido-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.pedido-tienda {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 2px;
}
.pedido-id {
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.25;
}
.pedido-fecha {
  margin-top: 2px;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
}
.estado-chip {
  font-weight: 700 !important;
  flex-shrink: 0;
}
.pedido-total {
  margin-top: 10px;
  font-size: 1.25rem;
  font-weight: 800;
}
.pedido-lineas {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.linea-resumen {
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.45;
}
.linea-cant {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.85);
  margin-right: 4px;
}
.linea-mas {
  margin-top: 2px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.45);
}
.pedido-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
}
.action-text {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}
.action-primary {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
}
</style>
